import axios from "axios";
import {
  getApiBaseUrl,
  getEngagementsBaseUrl,
  getTemplateGeneratorBaseUrl,
} from "src/config/endpoints";
import { message } from "antd";
import { buildAuthHeaders, getAuthToken } from "./httpUtils";
import { normalizeApiDetail, showApiDetailToast } from "src/utils/showApiDetailToast";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import type { ApiAudienceList } from "src/utils/audienceListLabels";

const API_BASE_URL = getApiBaseUrl();
const ENGAGEMENTS_BASE_URL = getEngagementsBaseUrl();
const TEMPLATE_GENERATOR_BASE_URL = getTemplateGeneratorBaseUrl();

// ========== Interfaces ==========

export interface DateRange {
  start_date: string;
  end_date: string;
}

export interface CardData {
  sent: number;
  opened: number;
  clicks: number;
  conversions: number;
}

export interface TimegraphData {
  campaign_name: string;
  start_date: string;
  end_date: string;
  last_updated: string;
  total_sent: number;
  opened: number;
  clicks: number;
  conversions: number;
  open_rate: number;
  click_rate: number;
  click_through_rate: number;
}

export interface OverviewResponse {
  status: string;
  data: {
    tenant_id: number;
    tenant_name: string;
    date_range: DateRange;
    sort_by: string;
    cards: CardData;
    summary: {
      total_campaigns: number;
      average_open_rate: number;
      average_click_rate: number;
      average_click_through_rate: number;
    };
    timegraph: TimegraphData[];
  };
}

export interface EngagementMetricsPayload {
  start_date: string;
  end_date: string;
  tenant_id?: number;
  sort_by?: string;
}

export interface EngagementCards {
  sent: number;
  opened: number;
  clicks: number;
  conversions: number;
  percentage_change: {
    sent: string;
    opened: string;
    clicks: string;
    conversions: string;
  };
}

export interface EngagementTimeGraph {
  status: string;
  message: string;
  tenant_id: number;
  categories: any[]; // You can replace `any` with a specific interface if known
}

export interface EngagementMetricsResponse {
  cards: EngagementCards;
  timegraph: EngagementTimeGraph;
}

interface CheckListExistsPayload {
  tenant_id: number;
  list_name: string;
}

interface CheckListExistsResponse {
  detail: string;
}

export interface CampaignOverviewResponse {
  status: string;
  campaigns?: any[];
  total_emails?: number;
  message?: string;
}

export interface CampaignStatsData {
  campaign_name: string;
  sendgrid_campaign_id: string;
  total_sent: number;
  delivered: number;
  bounces: number;
  opens: number;
  unique_opens: number;
  clicks: number;
  unique_clicks: number;
  unsubscribes: number;
  open_rate: number;
  click_rate: number;
  unsubscribe_rate: number;
  conversion_rate: number;
  email_forwarding_rate: number;
  new_users: number;
  returning_users: number;
}

export interface CampaignStatsResponse {
  campaigns: CampaignStatsData[];
  total_emails: number;
}

// ========== Send / Resend Campaign API ==========

export interface SendCampaignPayload {
  tenant_id: number;
  campaign_id: string;
  campaign_name: string;
}

export interface SendCampaignResponse {
  status: string;
  message: string;
}

// ========== Engagement API Call ==========

export const getEngagementMetrics = (payload: EngagementMetricsPayload) => {
  return axios.post<EngagementMetricsResponse>(
    `${ENGAGEMENTS_BASE_URL}/overview`,
    payload,
    { headers: buildAuthHeaders("api_key") }
  );
};

// ========== Check List Exists API ==========

export const checkListExists = (payload: CheckListExistsPayload) => {
  const token = getAuthToken("api_key");
  return axios.post<CheckListExistsResponse>(
    `${ENGAGEMENTS_BASE_URL}/check-list-exists`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

// ========== Check Campaign Exists API ==========

interface CheckCampaignExistsPayload {
  tenant_id: number;
  campaign_name: string;
}

interface CheckCampaignExistsResponse {
  detail: string;
}

export interface EngagementTemplatePayload {
  website_url?: string;
  category_links: string[];
  campaign_type: string;
  campaign_subtype: string;
  prompt: string;
  tone: string[];
  persona: string;
  seasonal_tag: string;
  offer?: string;
  cta_label: string;
}

export const checkCampaignExists = (payload: CheckCampaignExistsPayload) => {
  const token = getAuthToken("api_key");
  return axios.post<CheckCampaignExistsResponse>(
    `${ENGAGEMENTS_BASE_URL}/check-campaign-exists`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
};

// ========== Get Overview Data from ClickHouse ==========

export const getOverviewDataClickhouse = async (
  payload: EngagementMetricsPayload
): Promise<OverviewResponse> => {
  const token = getAuthToken("api_key");
  const response = await axios.post<OverviewResponse>(
    `${ENGAGEMENTS_BASE_URL}/overview-clickhouse`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

// EngagementAPIs.ts
export const getCampaignListsAndCompany = async (
  tenantId: string,
  apiKey: string
) => {
  const url = `${API_BASE_URL}/get-campaign-names-and-company?tenant_id=${tenantId}&api_key=${apiKey}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const data = await response.json();
  return data;
};

export const autopopulateCampaignData = async (
  tenantId: number,
  campaignId: string,
  api_key: string
) => {

  const res = await fetch(`${ENGAGEMENTS_BASE_URL}/autopopulate-campaigns`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
     // "api_key": api_key
      "Authorization": `Bearer ${api_key}` 

    },
    body: JSON.stringify({
      tenant_id: tenantId,
      sendgrid_campaign_id: campaignId,
    }),
  });

  if (!res.ok) {
    const errorDetails = await res.text();
    throw new Error(`API Error ${res.status}: ${errorDetails}`);
  }

  return res.json();
};


// create segment api
// engagementsApi.ts
export const createSegment = async (
  tenantId: string,
  segmentName: string,
  criteria: Record<string, unknown>
) => {
  const payload = {
    tenant_id: tenantId,
    segment_name: segmentName,
    criteria: criteria,
  };

  //console.log("About to send API request with payload:", payload);

  const response = await fetch(
   `${ENGAGEMENTS_BASE_URL}/create-segment`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("api_key")}`,
      },
      body: JSON.stringify(payload),
    }
  );

  //console.log("API Response status:", response.status);
  const data = await response.json();
  //console.log("API Response data:", data);

  if (!response.ok) {
    throw new Error(data.message || "Failed to create segment");
  }

  return data;
};

export const fetchCampaignListsAndCompany = async (
  tenantId: string,
  apiKey: string,
  lang?: ApiLang
): Promise<{
  list_names?: string[];
  lists?: ApiAudienceList[];
  company_name_engagements?: string;
}> => {
  const params = new URLSearchParams({
    tenant_id: tenantId,
    api_key: apiKey,
    lang: normalizeApiLang(lang ?? getApiLangFromI18n(i18n)),
  });
  const url = `${ENGAGEMENTS_BASE_URL}/get-campaign-names-and-company?${params.toString()}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch campaign lists and company");
  }

  return await response.json();
};

// ========== Get Campaign Overview  ==========
export const fetchCampaignsOverview = async (
  startDate: string,
  endDate: string
): Promise<CampaignOverviewResponse> => {
  const tenantId = localStorage.getItem("tenant_id");
  const apiKey = localStorage.getItem("api_key");

  if (!tenantId || !apiKey) {
    throw new Error("Missing tenant_id or api_key in localStorage");
  }

  const response = await fetch(
    `${ENGAGEMENTS_BASE_URL}/all-campaigns-overview`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        tenant_id: parseInt(tenantId),
        start_date: startDate,
        end_date: endDate,
        sort_by: "open_rate",
        show_archived: true,
      }),
    }
  );

  return response.json();
};


export async function deleteCampaign(campaignId: string) {
  const tenantId = localStorage.getItem("tenant_id");
  const apiKey = localStorage.getItem("api_key");

  if (!tenantId || !apiKey) {
    throw new Error("Tenant ID or API Key missing");
  }

  const response = await fetch(`${ENGAGEMENTS_BASE_URL}/delete-campaign`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      tenant_id: parseInt(tenantId),
      campaign_ids: [campaignId],
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to delete campaign");
  }

  return response.json();
}

export async function updateCampaignStatus(
  campaignId: string,
  status: "archive" | "active"
) {
  const tenantId = localStorage.getItem("tenant_id");
  const apiKey = localStorage.getItem("api_key");

  if (!tenantId || !apiKey) {
    throw new Error("Tenant ID or API Key missing");
  }

  const response = await fetch(
    `${ENGAGEMENTS_BASE_URL}/update-campaign-status`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        tenant_id: parseInt(tenantId),
        campaign_ids: [campaignId],
        status,
      }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to update campaign status");
  }

  return response.json();
}

export async function fetchCampaignStats(
  campaign_id: string,
  campaign_name: string
): Promise<CampaignStatsData | null> {
  const tenantIdStr = localStorage.getItem("tenant_id");
  const apiKey = localStorage.getItem("api_key");

  if (!apiKey) {
    console.error("Missing api_key in localStorage");
    return null;
  }

  const tenantId = parseInt(tenantIdStr ?? "0", 10);

  try {
    const response = await fetch(
      `${ENGAGEMENTS_BASE_URL}/campaigns-stats`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          campaign_id,
          campaign_name,
        }),
      }
    );

    const data: CampaignStatsResponse = await response.json();

    if (data.campaigns && data.campaigns.length > 0) {
      return data.campaigns[0];
    } else {
      console.warn("No campaign stats found");
      return null;
    }
  } catch (error) {
    console.error("Error fetching campaign stats:", error);
    return null;
  }
}

// ========== Upload List API ==========
export const uploadListAPI = async (
  formData: FormData
): Promise<{ status: string; message?: string; detail?: string }> => {
  const apiKey = localStorage.getItem("api_key");

  if (!apiKey) {
    throw new Error("Missing api_key in localStorage");
  }

  const response = await fetch(`${ENGAGEMENTS_BASE_URL}/upload-list`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
    },
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || "Failed to upload list");
  }

  return data;
};

// services/EngagementAPIs.ts
export const fetchCategories = async (lang?: ApiLang) => {
  const apiKey = getAuthToken("api_key");
  const tenantId = localStorage.getItem("tenant_id");

  if (!apiKey || !tenantId) {
    throw new Error("Missing API key or tenant ID");
  }

  const params = new URLSearchParams({
    api_key: apiKey,
    lang: normalizeApiLang(lang ?? getApiLangFromI18n(i18n)),
  });
  const res = await fetch(
    `${TEMPLATE_GENERATOR_BASE_URL}/fetch-website-categories?${params.toString()}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tenant_id: Number(tenantId),
      }),
    }
  );

  if (!res.ok) {throw new Error("Failed to fetch categories");}

  return res.json();
};

//genearte with ai email editor
interface GenerateTemplatePayload extends Record<string, unknown> {
  cta_url: string;
  prompt: string;
  tone: string[];
  persona: string;
  lang?: ApiLang;
}

export interface GenerateTemplateResult {
  success: boolean;
  template?: string | null;
  previewUrl?: string | null;
  design?: unknown;
  errorMessage?: string;
  /** When true, `showApiDetailToast` already showed the server `detail` — avoid duplicate `message.error`. */
  toastShown?: boolean;
}

export const generateTemplate = async (
  payload: GenerateTemplatePayload,
): Promise<GenerateTemplateResult> => {
  try {
    const apiKey = getAuthToken("api_key");
    const tenantId = localStorage.getItem("tenant_id");

    if (!apiKey || !tenantId) {
      message.error("Missing API key or tenant ID.");
      return { success: false, errorMessage: "Missing API key or tenant ID", toastShown: false };
    }

    if (!payload?.cta_url) {
      message.error("CTA URL is required.");
      return { success: false, errorMessage: "CTA URL required", toastShown: false };
    }

    if (!payload?.prompt) {
      message.error("Prompt cannot be empty.");
      return { success: false, errorMessage: "Prompt required", toastShown: false };
    }

    if (!payload?.tone?.length) {
      message.error("Please select at least one tone.");
      return { success: false, errorMessage: "Tone required", toastShown: false };
    }

    if (!payload?.persona) {
      message.error("Persona is required.");
      return { success: false, errorMessage: "Persona required", toastShown: false };
    }

    const { lang: payloadLang, ...payloadWithoutLang } = payload;
    const fullPayload = {
      ...payloadWithoutLang,
      tenant_id: Number(tenantId),
      lang: normalizeApiLang(payloadLang ?? getApiLangFromI18n(i18n)),
    };

    const response = await fetch(
      `${TEMPLATE_GENERATOR_BASE_URL}/generate-template?api_key=${apiKey}`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(fullPayload),
      }
    );

    if (!response.ok) {
      let errJson: Record<string, unknown> | null = null;
      try {
        errJson = (await response.json()) as Record<string, unknown>;
      } catch {
        errJson = null;
      }
      const apiDetail = normalizeApiDetail(errJson?.detail);
      const errorMessage =
        apiDetail ||
        `Template generation failed (${response.status} ${response.statusText || ""})`.trim();
      showApiDetailToast(apiDetail || errorMessage);
      return {
        success: false,
        errorMessage,
        toastShown: true,
      };
    }

    const data = await response.json();

    if (data) {
      let design: unknown =
        data?.design || data?.template_json || data?.unlayer_json || data?.json || null;

      if (!design && data?.template && typeof data.template === "object") {
        design = data.template;
      }

      const htmlCandidate =
        typeof data?.template === "string"
          ? data.template
          : typeof data?.html === "string"
          ? data.html
          : null;

      const html: string | null = htmlCandidate;

      const previewUrl: string | null =
        typeof data?.preview_url === "string" && data.preview_url.length > 0
          ? data.preview_url
          : typeof html === "string" && html.length > 0
          ? URL.createObjectURL(new Blob([html], { type: "text/html" }))
          : null;

      message.success("Template generated successfully!");

      if (previewUrl || html || design) {
        return {
          success: true,
          template: html,
          previewUrl,
          design,
        };
      }
    }

    message.error("AI returned no template. Try again.");
    return {
      success: false,
      errorMessage: "No template returned",
      toastShown: false,
    };

  } catch (error: unknown) {
    message.error("Unexpected error occurred while generating template.");

    return {
      success: false,
      errorMessage: "Unexpected error",
      toastShown: false,
    };
  }
};
